import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Slide, Fade } from "@mui/material";
import { useTranslation } from "react-i18next";
import useWindowDimensions from "../../../utils/getHeightWidth";
import { changeTitle } from "../../../state/slices/header";
import CustomCards from "../../common/CustomCards/CustomCards";
import SubHeaderComponent from "../../common/RDTSubHeaderComponent/SubHeaderComponent";
import { ReportCardsData } from "../../../utils/cardsData";
import CustomColumns from "../../common/CustomColumns/CustomColumns";
import {
  delayTime,
  slideDirection,
  fadedelayTime,
} from "../../../utils/transitionEffectParams";
import reportsIcon from "./icons/icon.svg";
import ListHeading from "../../common/RDTListHeading/ListHeading";
import { RefreshReport } from "../../../utils/sidebar";
function ReportSection() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [slideChecked, setSlideChecked] = useState(false);
  const { height } = useWindowDimensions();
  const [search, setSearch] = useState("");
  const [showLayoutSwitcher, setShowLayoutSwitcher] = useState("");
  const [filterBy, setFilterBy] = useState("");
  
  // Get capabilities at component level (hooks must be called at top level)
  const capabilities = useSelector(
    (state) => state.user?.user?.role?.capabilities || {}
  );
  
  // Recalculate reports when language or capabilities change
  const reports = useMemo(() => ReportCardsData(t, capabilities), [t, i18n.language, capabilities]);
  const [filteredReportData, setFilteredReportData] = useState(reports);
  const [viewlayoutSwitcher, setLayoutSwitcher] = useState(() => {
    return localStorage.getItem("viewType_Reports") || "cardsView";
  });
  const title_heading = useSelector((state) => state.header.title);
  const page_heading = t("reports.title");
  let datatable_height = height - 115 + "px";
  const UserColumns = [
    { label: t("reports.databaseReports"), value: "database" },
    { label: t("reports.serverReports"), value: "server" },
    { label: t("reports.webAppReports"), value: "webAppReports" },
    { label: t("reports.userReports"), value: "userLoginReports" },
    { label: t("reports.roleReports"), value: "role" },
    { label: t("reports.groupReports"), value: "group" },
    { label: t("reports.ticketReports"), value: "ticketsReport" },
    { label: t("reports.passwordRotationReports"), value: "password" },
    { label: t("reports.clientCredentialsReports"), value: "clientCredentials" },
    { label: t("reports.applicationCredentialsAccessReports"), value: "applicationCredentialsAccess" },
    { label: t("reports.cloudDiscoveryReports"), value: "cloudDiscovery" },
    { label: t("reports.policyReports"), value: "policyReports" },
    { label: t("reports.otDeviceReports"), value: "otDevice" },
  ];

  useEffect(() => {
    document.title = page_heading;
    setSlideChecked(true);
  }, [page_heading]);

  useEffect(() => {
    dispatch(changeTitle("Reports"));
  }, []);

  useEffect(() => {
    const filteredData = reports.filter((item) => {
      const searchs = search.replaceAll(/[\\\*\(\)\+\[\?]/g, "").toLowerCase();

      const filterConditions = {
        database: () => item.title.toLowerCase().includes("database"),
        webAppReports: () => item.title.toLowerCase().includes("web"),
        ticketsReport: () => item.title.toLowerCase().includes("tickets"),
        server: () => item.title.toLowerCase().includes("server"),
        otDevice: () => item.title.toLowerCase().includes("ot device"),
        userLoginReports: () => item.title.toLowerCase().includes("user"),
        password: () => item.title.toLowerCase().includes("password"),
        role: () => item.title.toLowerCase().includes("role"),
        group: () => item.title.toLowerCase().includes("group"),
        clientCredentials: () => item.title.toLowerCase().includes("client credentials"),
        applicationCredentialsAccess: () => item.title.toLowerCase().includes("application credentials access"),
        cloudDiscovery: () => item.title.toLowerCase().includes("cloud discovery"),
        policyReports: () => item.title.toLowerCase().includes("policy"),
      };

      if (filterBy === "" || filterBy === "all") {
        return Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchs)
        );
      }

      if (filterConditions[filterBy]) {
        return (
          filterConditions[filterBy]() &&
          Object.values(item).some((val) =>
            String(val).toLowerCase().includes(searchs)
          )
        );
      } else {
        return (
          item[filterBy] &&
          String(item[filterBy]).toLowerCase().includes(searchs)
        );
      }
    });
    setFilteredReportData(filteredData);
  }, [search, filterBy, reports]);

  useEffect(() => {
    localStorage.setItem("viewType_Reports", viewlayoutSwitcher);
  }, [viewlayoutSwitcher]);
  const handleSearch = (event) => {
    event.preventDefault();
  };
  useEffect(() => {
    const handleRefreshData = () => {
      setFilteredReportData(reports);
      setFilterBy("all");
      setSearch("");
    };
    window.addEventListener(RefreshReport, handleRefreshData);
    return () => {
      window.removeEventListener(RefreshReport, handleRefreshData);
    };
  }, [reports]);

  return (
    <>
      <Slide timeout={delayTime} direction={slideDirection} in={slideChecked}>
        <div>
          <Fade timeout={fadedelayTime} in={slideChecked}>
            <div>
              <div className="main_content_container p-3 mx-auto w-100">
                <div
                  className="position-relative bg-white overflow-auto"
                  style={{ height: `${datatable_height}` }}
                >
                  <div className="configure_mfa_heading border-bottom ">
                    <div className="pt-1 configure_mfa_heading  d-flex flex-row justify-content-between w-100">
                      <div className="d-flex align-items-center fw-300 ff-poppins pt-2 pb-1 text-wrap ms-4 ps-2">
                        <ListHeading
                          dataTableHeadingText={t("reports.title")}
                          dataTableHeadingIcon={reportsIcon}
                        />
                        <br />
                      </div>
                    </div>
                  </div>{" "}
                  <SubHeaderComponent
                    filterBy={filterBy}
                    setFilterBy={setFilterBy}
                    search={search}
                    handleSearch={handleSearch}
                    setSearch={setSearch}
                    filterOptions={UserColumns}
                    placeholder={t("reports.title")}
                    generalButton={true}
                    showLayoutSwitcher={setShowLayoutSwitcher}
                    viewLayoutSwitcher={viewlayoutSwitcher}
                    setLayoutSwitcher={setLayoutSwitcher}
                  />
                  <div
                    className="overflow-auto"
                    style={{ height: `${height - 250}px` }}
                  >
                    <div className="ps-4 ms-3 pe-5 pb-4 pt-4">
                      {!filteredReportData ||
                      filteredReportData.length === 0 ? (
                        <div className="d-flex pt-3 justify-content-center ff-pam no-records-message">
                          {t("reports.thereAreNoRecordsToDisplay")}
                        </div>
                      ) : viewlayoutSwitcher === "cardsView" ? (
                        <CustomCards cardData={filteredReportData} showLogoContainer={false} />
                      ) : (
                        <CustomColumns columnData={filteredReportData} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Fade>
        </div>
      </Slide>
    </>
  );
}
export default ReportSection;
